import { useEffect, useMemo, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import * as THREE from "three";
import {
  followCamera, replayBearing,
  type TrackPoint, type ReplayPoint, type CameraPose,
} from "./FlightReplay3D.motion";
import { buildReplayFleet, sampleFleet, followedFlight, type ReplayFlight } from "./FlightReplay3D.fleet";
import { ReplayTrail } from "./FlightReplay3D.trail";

const SPEED_OPTIONS = [1, 8, 16, 32, 64];
const DEFAULT_SPEED = 16;
const TERRAIN_EXAGGERATION = 1;

interface LocalFrame {
  originLngLat: [number, number];
  originGroundAltitude: number;
  toLocal(point: ReplayPoint): { x: number; y: number; z: number };
}

interface FlightReplay3DProps {
  flightTrack?: TrackPoint[];
  flights?: ReplayFlight[];
  onClose: () => void;
  shareUrl?: string;
}

function formatReplayTime(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function buildLocalFrame(track: ReplayPoint[]): LocalFrame | null {
  if (!track.length) {
    return null;
  }

  const originPoint = track[0];
  const originLatRad = (originPoint.coordinates.lat * Math.PI) / 180;
  const earthRadius = 6371000;
  const originGroundAltitude = originPoint.groundAltitude;

  return {
    originLngLat: [originPoint.coordinates.lon, originPoint.coordinates.lat],
    originGroundAltitude,
    toLocal(point: ReplayPoint) {
      const deltaLon =
        ((point.coordinates.lon - originPoint.coordinates.lon) * Math.PI) / 180;
      const deltaLat =
        ((point.coordinates.lat - originPoint.coordinates.lat) * Math.PI) / 180;

      const eastMeters = earthRadius * deltaLon * Math.cos(originLatRad);
      const northMeters = earthRadius * deltaLat;
      const upMeters = point.altitude - originGroundAltitude;

      return {
        x: eastMeters,
        y: northMeters,
        z: upMeters,
      };
    },
  };
}

function createMapStyle(): maplibregl.StyleSpecification {
  return {
    version: 8,
    sources: {
      imagery: {
        type: "raster",
        tiles: [
          "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
        ],
        tileSize: 256,
        attribution:
          'Imagery &copy; <a href="https://www.esri.com/">Esri</a>, Maxar, Earthstar Geographics, and the GIS User Community',
      },
      terrain: {
        type: "raster-dem",
        tiles: ["https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png"],
        tileSize: 256,
        encoding: "terrarium",
        maxzoom: 15,
        attribution:
          'Elevation &copy; <a href="https://www.mapzen.com/blog/terrain-tile-service/">Mapzen Terrarium</a>',
      },
    },
    layers: [
      {
        id: "imagery",
        type: "raster",
        source: "imagery",
      },
    ],
  };
}

export function FlightReplay3D({ flightTrack, flights, onClose, shareUrl }: FlightReplay3DProps) {
  const fleet = useMemo(() => buildReplayFleet(flights ?? [{
    id: "flight", pilotId: 0, label: "Glider", color: "#ffad42", track: flightTrack ?? [],
  }]), [flightTrack, flights]);
  const replayTrack = useMemo(() => fleet.tracks.flatMap(track => track.points), [fleet]);
  const pilots = useMemo(() => [...new Map(fleet.tracks.map(track => [track.pilotId, track])).values()], [fleet]);
  const [pilotId, setPilotId] = useState(pilots[0]?.pilotId ?? 0);
  const pilotRef = useRef(pilotId);
  const localFrame = useMemo(() => buildLocalFrame(replayTrack), [replayTrack]);
  const durationSeconds = fleet.duration;
  const [speed, setSpeed] = useState(DEFAULT_SPEED);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const [following, setFollowing] = useState(true);
  const [linkCopied, setLinkCopied] = useState(false);
  const [shareError, setShareError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef(0);
  const followingRef = useRef(true);
  const cameraRef = useRef<CameraPose | null>(null);
  const playbackRef = useRef({ speed, paused });

  useEffect(() => { playbackRef.current = { speed, paused }; }, [speed, paused]);
  useEffect(() => {
    if (!linkCopied) return;
    const timeout = setTimeout(() => setLinkCopied(false), 2000);
    return () => clearTimeout(timeout);
  }, [linkCopied]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    if (!containerRef.current || !replayTrack.length || !localFrame) return;
    progressRef.current = 0;
    followingRef.current = true;
    cameraRef.current = null;
    playbackRef.current = { speed: DEFAULT_SPEED, paused: false };
    setSpeed(DEFAULT_SPEED);
    setProgress(0);
    setPaused(false);
    setFollowing(true);

    pilotRef.current = fleet.tracks[0].pilotId;
    setPilotId(pilotRef.current);
    const first = replayTrack[0];
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: createMapStyle(),
      center: [first.coordinates.lon, first.coordinates.lat],
      elevation: first.altitude,
      centerClampedToGround: false,
      zoom: 13.6,
      pitch: 65,
      bearing: replayBearing(fleet.tracks[0].points, 0, 0),
      hash: false,
      maxPitch: 85,
      attributionControl: {},
    });
    map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), "top-right");
    // A deliberate drag releases the camera; zooming preserves follow mode.
    const suspendFollow = () => {
      followingRef.current = false;
      setFollowing(false);
    };
    const releaseFollow = (event: { originalEvent?: unknown }) => {
      if (event.originalEvent) suspendFollow();
    };
    // Release before the first drag frame, so camera updates cannot cancel it.
    map.getCanvas().addEventListener("pointerdown", suspendFollow);
    map.on("dragstart", releaseFollow);
    map.on("rotatestart", releaseFollow);
    map.on("pitchstart", releaseFollow);

    let animation = 0;
    map.on("load", () => {
      map.setTerrain({ source: "terrain", exaggeration: TERRAIN_EXAGGERATION });
      const camera = new THREE.Camera();
      const scene = new THREE.Scene();
      let renderer: THREE.WebGLRenderer | null = null;
      const overlays = fleet.tracks.map(track => {
        const trail = new ReplayTrail(track.points.map(point => {
          const local = localFrame.toLocal(point);
          return [local.x, local.y, local.z];
        }), track.color);
        scene.add(trail.object);
        const glider = new THREE.Mesh(
          new THREE.SphereGeometry(12, 16, 16),
          new THREE.MeshBasicMaterial({ color: track.color, depthTest: false, depthWrite: false }),
        );
        glider.renderOrder = 11;
        glider.visible = false;
        scene.add(glider);
        return { trail, glider };
      });
      const customLayer: maplibregl.CustomLayerInterface = {
        id: "flight-replay-3d-overlay",
        type: "custom",
        renderingMode: "3d",
        onAdd(_map, gl) {
          renderer = new THREE.WebGLRenderer({ canvas: map.getCanvas(), context: gl, antialias: true });
          renderer.autoClear = false;
        },
        render(_gl, args) {
          // IGC altitude is absolute: tile loading must not move the flight vertically.
          const origin = maplibregl.MercatorCoordinate.fromLngLat(localFrame.originLngLat, localFrame.originGroundAltitude);
          const scale = origin.meterInMercatorCoordinateUnits();
          const projection = new THREE.Matrix4().fromArray(args.defaultProjectionData.mainMatrix);
          const transform = new THREE.Matrix4().makeTranslation(origin.x, origin.y, origin.z)
            .scale(new THREE.Vector3(scale, -scale, scale));
          camera.projectionMatrix = projection.multiply(transform);
          overlays.forEach(({ trail }) => trail.resize(map.getCanvas().clientWidth, map.getCanvas().clientHeight));
          renderer!.resetState();
          renderer!.render(scene, camera);
        },
        onRemove() {
          overlays.forEach(({ trail, glider }) => {
            trail.dispose();
            glider.geometry.dispose();
            glider.material.dispose();
          });
          renderer?.dispose();
        },
      };
      map.addLayer(customLayer);
      let previousTime = performance.now();
      let lastUIUpdate = 0;
      const frame = (now: number) => {
        // Do not jump through the flight after a background tab resumes.
        const delta = Math.min((now - previousTime) / 1000, 0.1);
        previousTime = now;
        const playback = playbackRef.current;
        if (!playback.paused && durationSeconds > 0) {
          progressRef.current = Math.min(1, progressRef.current + delta * playback.speed / durationSeconds);
          if (progressRef.current === 1) {
            playbackRef.current.paused = true;
            setPaused(true);
          }
        }
        const current = progressRef.current;
        const samples = sampleFleet(fleet, current);
        samples.forEach(({ sample, started }, index) => {
          const { trail, glider } = overlays[index];
          const local = localFrame.toLocal(sample.point);
          trail.object.visible = glider.visible = started;
          trail.update(sample.segment, [local.x, local.y, local.z]);
          glider.position.set(local.x, local.y, local.z);
        });
        const followed = followedFlight(samples, pilotRef.current)!;
        const sample = followed.sample;
        if (followingRef.current) {
          const target = {
            lon: sample.point.coordinates.lon,
            lat: sample.point.coordinates.lat,
            altitude: sample.point.altitude,
            bearing: replayBearing(followed.points, followed.progress, cameraRef.current?.bearing ?? map.getBearing()),
          };
          const center = map.getCenter();
          const previous = cameraRef.current ?? {
            lon: center.lng, lat: center.lat, altitude: map.getCenterElevation(), bearing: map.getBearing(),
          };
          const pose = followCamera(previous, target, delta);
          cameraRef.current = pose;
          map.jumpTo({ center: [pose.lon, pose.lat], elevation: pose.altitude, bearing: pose.bearing });
        }
        if (now - lastUIUpdate >= 100 || current === 1) {
          setProgress(current);
          lastUIUpdate = now;
        }
        map.triggerRepaint();
        animation = requestAnimationFrame(frame);
      };
      animation = requestAnimationFrame(frame);
    });
    return () => {
      cancelAnimationFrame(animation);
      map.getCanvas().removeEventListener("pointerdown", suspendFollow);
      map.remove();
    };
  }, [fleet, replayTrack, localFrame, durationSeconds]);

  const currentPoint = useMemo(
    () => followedFlight(sampleFleet(fleet, progress), pilotId)?.sample.point ?? null,
    [fleet, progress, pilotId],
  );
  const elapsedSeconds = progress * durationSeconds;

  return (
    <div className="replay-fullscreen" role="dialog" aria-modal="true" aria-label={flights ? "3D adventure replay" : "3D flight replay"}>
        <div className="replay-canvas-shell">
          <div ref={containerRef} className="replay-map" />

          {currentPoint ? (
            <div className="replay-stats">
              <div>
                <strong>Altitude</strong>
                <span>{Math.round(currentPoint.altitude)} m</span>
              </div>
              <div>
                <strong>Height AGL</strong>
                <span>{Math.round(Math.max(currentPoint.altitude - currentPoint.groundAltitude, 0))} m</span>
              </div>
            </div>
          ) : null}
        </div>

        {flights && (
          <div className="replay-pilots">
            <label className="replay-speed">
              <span>Camera follows</span>
              <select value={pilotId} onChange={event => {
                const selected = Number(event.target.value);
                pilotRef.current = selected;
                setPilotId(selected);
                followingRef.current = true;
                setFollowing(true);
                cameraRef.current = null;
              }}>
                {pilots.map(pilot => <option key={pilot.pilotId} value={pilot.pilotId}>{pilot.label}</option>)}
              </select>
            </label>
            <ul aria-label="Pilot colors">
              {pilots.map(pilot => <li key={pilot.pilotId}>
                <span className="replay-pilot-color" style={{ backgroundColor: pilot.color }} />
                {pilot.label}
              </li>)}
            </ul>
          </div>
        )}

        <div className="replay-toolbar">
          {shareUrl && (
            <div className="replay-share">
              <button
                type="button"
                className="replay-follow"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(new URL(shareUrl, window.location.href).href);
                    setLinkCopied(true);
                    setShareError(false);
                  } catch {
                    setShareError(true);
                  }
                }}
              >
                {linkCopied ? "Link copied" : "Copy 3D link"}
              </button>
              {shareError && (
                <p role="status">Copy unavailable. <a href={shareUrl}>Right-click this replay link to copy it.</a></p>
              )}
            </div>
          )}
          <button type="button" className="replay-icon-button replay-close" onClick={onClose} aria-label="Close 3D replay">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M18.3 5.71 12 12l6.3 6.29-1.41 1.41L10.59 13.41 4.29 19.7 2.88 18.29 9.17 12 2.88 5.71 4.29 4.3l6.3 6.29 6.29-6.3z" />
            </svg>
          </button>
        </div>

        <div className="replay-controls">
          <button
            type="button"
            className="replay-icon-button replay-button"
            onClick={() => {
              if (progress >= 1) {
                progressRef.current = 0;
                setProgress(0);
              }
              setPaused((current) => !current);
            }}
            aria-label={paused ? "Resume replay" : "Pause replay"}
          >
            {paused ? (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M8 5v14l11-7z" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6 5h4v14H6zm8 0h4v14h-4z" />
              </svg>
            )}
          </button>

          <button
            type="button"
            className="replay-follow"
            aria-pressed={following}
            onClick={() => {
              const next = !following;
              followingRef.current = next;
              setFollowing(next);
              cameraRef.current = null;
            }}
          >
            {following ? "Following glider" : "Follow glider"}
          </button>

          <label className="replay-speed">
            <span>Speed</span>
            <select value={speed} onChange={(event) => setSpeed(Number(event.target.value))}>
              {SPEED_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}x
                </option>
              ))}
            </select>
          </label>

          <label className="replay-progress">
            <span>Progress</span>
            <input
              type="range"
              min="0"
              max="1000"
              value={Math.round(progress * 1000)}
              onChange={(event) => {
                const next = Number(event.target.value) / 1000;
                progressRef.current = next;
                setProgress(next);
                cameraRef.current = null;
              }}
            />
          </label>

          <div className="replay-timing">
            <strong>{formatReplayTime(elapsedSeconds)}</strong>
            <span>/ {formatReplayTime(durationSeconds)}</span>
          </div>
        </div>
    </div>
  );
}
